import { useState } from "react";
import { PurchaseOrder, POComment } from "@po-management/types";
import { useApiClient } from "../auth/useApiClient";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useAppRole } from "../auth/useAppRole";
import { useComments } from "./hooks/useComments";
import { usePdfDownload } from "./hooks/usePdfDownload";
import { useAuditLog } from "../admin/useAuditLog";

interface PODetailProps {
  po: PurchaseOrder;
  onDone: () => void;
}

function row(label: string, value: React.ReactNode): JSX.Element {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, marginBottom: 10, fontSize: 14 }}>
      <span style={{ color: "#666", fontWeight: 500 }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function section(title: string): JSX.Element {
  return <h3 style={{ margin: "24px 0 12px", fontSize: 14, color: "#5B6A8A", borderBottom: "1px solid #eee", paddingBottom: 6 }}>{title}</h3>;
}

function CommentBubble({ comment }: { comment: POComment }): JSX.Element {
  const colors: Record<string, { bg: string; border: string; label: string }> = {
    Comment: { bg: "#f5f6fa", border: "#dde", label: "" },
    ApprovalNote: { bg: "#f0faf4", border: "#a7d7b8", label: "✓ Approved" },
    RejectionNote: { bg: "#fff5f5", border: "#fcc", label: "✗ Rejected" },
    SystemEvent: { bg: "#fffbe6", border: "#ffe58f", label: "System" },
  };
  const style = colors[comment.type] ?? colors.Comment;
  return (
    <div style={{ marginBottom: 12, padding: "10px 14px", background: style.bg, border: `1px solid ${style.border}`, borderRadius: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12, color: "#888" }}>
        <span><strong style={{ color: "#444" }}>{comment.authorName}</strong>{style.label && <span style={{ marginLeft: 8, fontWeight: 600, color: comment.type === "ApprovalNote" ? "#059669" : comment.type === "RejectionNote" ? "#dc2626" : "#7d4e00" }}>{style.label}</span>}</span>
        <span>{new Date(comment.createdAt).toLocaleString()}</span>
      </div>
      <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{comment.message}</div>
    </div>
  );
}

export function PODetail({ po: initialPO, onDone }: PODetailProps): JSX.Element {
  const { patch } = useApiClient();
  const currentUser = useCurrentUser();
  const { isDirector, isSuperAdmin } = useAppRole();
  const { comments, loading: commentsLoading, addComment } = useComments(initialPO.id);

  const { downloading, downloadPdf, error: pdfError } = usePdfDownload();

  const [po, setPO] = useState<PurchaseOrder>(initialPO);
  const { entries: auditEntries, loading: auditLoading } = useAuditLog(isSuperAdmin ? po.id : "");
  const [acting, setActing] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);

  const isAssignedApprover = currentUser?.oid === po.assignedApproverOid;
  const canAct = (isDirector || isSuperAdmin) && isAssignedApprover && po.status === "PendingApproval";
  const canSend = po.status === "Approved" && (currentUser?.oid === po.createdByOid || isSuperAdmin);
  const canReceive = po.status === "Sent" && (currentUser?.oid === po.createdByOid || isSuperAdmin);

  async function handleApprove(): Promise<void> {
    setActing(true); setApiError(null);
    try {
      const res = await patch(`/api/pos/${po.id}/approve`, {});
      const body = await res.json() as { success: boolean; data?: PurchaseOrder; error?: string };
      if (!res.ok || !body.success) { setApiError(body.error ?? "Failed to approve"); return; }
      setPO(body.data!);
      onDone();
    } catch { setApiError("Network error"); }
    finally { setActing(false); }
  }

  async function handleReject(): Promise<void> {
    if (!rejectionNote.trim()) return;
    setActing(true); setApiError(null);
    try {
      const res = await patch(`/api/pos/${po.id}/reject`, { rejectionNote });
      const body = await res.json() as { success: boolean; data?: PurchaseOrder; error?: string };
      if (!res.ok || !body.success) { setApiError(body.error ?? "Failed to reject"); return; }
      setPO(body.data!);
      onDone();
    } catch { setApiError("Network error"); }
    finally { setActing(false); }
  }

  async function handleSend(): Promise<void> {
    setActing(true); setApiError(null);
    try {
      const res = await patch(`/api/pos/${po.id}/send`, {});
      const body = await res.json() as { success: boolean; data?: PurchaseOrder; error?: string };
      if (!res.ok || !body.success) { setApiError(body.error ?? "Failed to send"); return; }
      setPO(body.data!);
      setShowSendConfirm(false);
    } catch { setApiError("Network error"); }
    finally { setActing(false); }
  }

  async function handleReceive(): Promise<void> {
    setActing(true); setApiError(null);
    try {
      const res = await patch(`/api/pos/${po.id}/receive`, {});
      const body = await res.json() as { success: boolean; data?: PurchaseOrder; error?: string };
      if (!res.ok || !body.success) { setApiError(body.error ?? "Failed to mark as received"); return; }
      setPO(body.data!);
    } catch { setApiError("Network error"); }
    finally { setActing(false); }
  }

  async function handleAddComment(): Promise<void> {
    if (!newComment.trim()) return;
    const ok = await addComment(newComment.trim());
    if (ok) setNewComment("");
  }

  const totalValue = Number(po.totalValue);
  const paymentAlert = po.paymentTermsAlertFlag || po.paymentTermsDays < 60;

  return (
    <div style={{ padding: "2rem", maxWidth: 800, fontFamily: "sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <button onClick={onDone} style={{ background: "none", border: "none", color: "#5B6A8A", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 0 }}>←</button>
        <div>
          <h2 style={{ margin: 0, color: "#5B6A8A" }}>{po.poNumber}</h2>
          <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 10, background: "#5B6A8A20", color: "#5B6A8A", fontWeight: 600 }}>{po.status}</span>
        </div>
        <button
          onClick={() => void downloadPdf(po.id, po.poNumber)}
          disabled={downloading}
          style={{ marginLeft: "auto", padding: "7px 14px", background: "#1B3A6B", color: "#fff", border: "none", borderRadius: 4, cursor: downloading ? "default" : "pointer", fontSize: 13, opacity: downloading ? 0.7 : 1 }}
        >
          {downloading ? "Generating…" : "⬇ Download PDF"}
        </button>
      </div>

      {apiError && <div style={{ padding: "10px 14px", background: "#fff0f0", border: "1px solid #fcc", borderRadius: 4, color: "#c00", marginBottom: 16, fontSize: 13 }}>{apiError}</div>}
      {pdfError && <div style={{ padding: "10px 14px", background: "#fff0f0", border: "1px solid #fcc", borderRadius: 4, color: "#c00", marginBottom: 16, fontSize: 13 }}>{pdfError}</div>}

      {/* Payment terms alert */}
      {paymentAlert && (
        <div style={{ padding: "10px 14px", background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 4, marginBottom: 16, fontSize: 13 }}>
          <strong style={{ color: "#7d4e00" }}>⚠ Payment Terms Alert:</strong> Payment terms are {po.paymentTermsDays} days (below 60).
          {po.paymentTermsJustification && <div style={{ marginTop: 4, color: "#555" }}>Justification: {po.paymentTermsJustification}</div>}
        </div>
      )}

      {/* Vendor */}
      {section("Vendor")}
      {row("Vendor", po.vendorNameSnapshot)}
      {row("Address", po.vendorAddressSnapshot)}
      {row("VAT / Tax Office", `${po.vendorVatSnapshot} / ${po.vendorTaxOfficeSnapshot}`)}
      {row("Contact Name", po.vendorContactName)}
      {row("Contact Email", po.vendorContactEmail)}

      {/* Ordering Party */}
      {section("Ordering Party")}
      {row("Language", po.orderingLanguage)}
      {row("Contact Name", po.contactName)}
      {po.contactRole && row("Title / Role", po.contactRole)}
      {row("Contact Email", po.contactEmail)}

      {/* Dates & Terms */}
      {section("Dates & Payment Terms")}
      {row("Document Date", String(po.documentDate).slice(0, 10))}
      {row("Delivery Date", String(po.deliveryDate).slice(0, 10))}
      {row("Payment Terms", `${po.paymentTermsDays} days`)}
      {po.agreementRef && row("Agreement Ref", po.agreementRef)}
      {po.specialTerms && row("Special Terms", po.specialTerms)}

      {/* Service Lines */}
      {section("Service Lines")}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
        <thead>
          <tr style={{ background: "#f5f6fa", fontSize: 12, color: "#666" }}>
            <th style={{ padding: "8px 10px", textAlign: "left" }}>#</th>
            <th style={{ padding: "8px 10px", textAlign: "left" }}>Description</th>
            <th style={{ padding: "8px 10px", textAlign: "right" }}>Qty</th>
            <th style={{ padding: "8px 10px", textAlign: "right" }}>Unit Price</th>
            <th style={{ padding: "8px 10px", textAlign: "right" }}>Net Total</th>
          </tr>
        </thead>
        <tbody>
          {(po.serviceLines ?? []).map((l) => (
            <tr key={l.id} style={{ borderBottom: "1px solid #eee", fontSize: 13 }}>
              <td style={{ padding: "8px 10px" }}>{l.itemNo}</td>
              <td style={{ padding: "8px 10px" }}>{l.name}</td>
              <td style={{ padding: "8px 10px", textAlign: "right" }}>{l.qty}</td>
              <td style={{ padding: "8px 10px", textAlign: "right" }}>€ {Number(l.unitPrice).toFixed(2)}</td>
              <td style={{ padding: "8px 10px", textAlign: "right" }}>€ {Number(l.netTotal).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 600, fontSize: 14, borderTop: "2px solid #dde" }}>
            <td colSpan={4} style={{ padding: "10px 10px", textAlign: "right", color: "#5B6A8A" }}>Total</td>
            <td style={{ padding: "10px 10px", textAlign: "right", color: "#5B6A8A" }}>€ {totalValue.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      {po.classification !== "None" && row("Classification", po.classification)}

      {/* Approver info */}
      {po.assignedApproverName && (
        <>
          {section("Approval")}
          {row("Assigned Approver", po.assignedApproverName)}
          {po.submittedAt && row("Submitted", new Date(po.submittedAt).toLocaleString())}
          {po.approvedAt && row("Approved", new Date(po.approvedAt).toLocaleString())}
          {po.sentAt && row("Sent to Vendor", new Date(po.sentAt).toLocaleString())}
          {po.receivedAt && row("Received", new Date(po.receivedAt).toLocaleString())}
        </>
      )}

      {/* Comment thread */}
      {section("Internal Comments")}
      {commentsLoading
        ? <p style={{ fontSize: 13, color: "#888" }}>Loading comments...</p>
        : comments.length === 0
          ? <p style={{ fontSize: 13, color: "#888" }}>No comments yet.</p>
          : comments.map((c) => <CommentBubble key={c.id} comment={c} />)
      }

      {/* Audit Log — SuperAdmin only */}
      {isSuperAdmin && (
        <>
          {section("Audit Log")}
          {auditLoading
            ? <p style={{ fontSize: 13, color: "#888" }}>Loading audit log…</p>
            : auditEntries.length === 0
              ? <p style={{ fontSize: 13, color: "#888" }}>No audit entries.</p>
              : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 8 }}>
                  <thead>
                    <tr style={{ background: "#f5f6fa" }}>
                      <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 12, color: "#666", fontWeight: 600 }}>Timestamp</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 12, color: "#666", fontWeight: 600 }}>Actor</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 12, color: "#666", fontWeight: 600 }}>Action</th>
                      <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 12, color: "#666", fontWeight: 600 }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEntries.map((e) => (
                      <tr key={e.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "6px 10px", color: "#888", whiteSpace: "nowrap" }}>{new Date(e.createdAt).toLocaleString()}</td>
                        <td style={{ padding: "6px 10px" }}>{e.actorName}</td>
                        <td style={{ padding: "6px 10px", fontWeight: 600, color: "#5B6A8A" }}>{e.action}</td>
                        <td style={{ padding: "6px 10px", color: "#666", fontSize: 12 }}>{e.data ? JSON.stringify(e.data) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          }
        </>
      )}

      {/* Add comment */}
      <div style={{ marginTop: 12 }}>
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add an internal comment..."
          rows={3}
          style={{ width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, boxSizing: "border-box", resize: "vertical", fontSize: 13 }}
        />
        <button
          onClick={() => void handleAddComment()}
          disabled={!newComment.trim()}
          style={{ marginTop: 6, padding: "7px 16px", background: newComment.trim() ? "#5B6A8A" : "#ccc", color: "#fff", border: "none", borderRadius: 4, cursor: newComment.trim() ? "pointer" : "default", fontSize: 13 }}
        >
          Add Comment
        </button>
      </div>

      {/* Approve / Reject actions — Director only, PendingApproval only */}
      {canAct && (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: "2px solid #5B6A8A30" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#5B6A8A" }}>Approval Decision</h3>

          {!showRejectForm ? (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={() => void handleApprove()}
                disabled={acting}
                style={{ padding: "10px 28px", background: "#059669", color: "#fff", border: "none", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: acting ? "default" : "pointer" }}
              >
                {acting ? "Processing..." : "✓ Approve"}
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                disabled={acting}
                style={{ padding: "10px 28px", background: "#fff", border: "2px solid #dc2626", color: "#dc2626", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: acting ? "default" : "pointer" }}
              >
                ✗ Reject
              </button>
            </div>
          ) : (
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Rejection note (required)</label>
              <textarea
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                placeholder="Explain why this PO is being rejected..."
                rows={4}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #fcc", borderRadius: 4, boxSizing: "border-box", resize: "vertical", fontSize: 13 }}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button
                  onClick={() => void handleReject()}
                  disabled={acting || !rejectionNote.trim()}
                  style={{ padding: "9px 20px", background: rejectionNote.trim() ? "#dc2626" : "#ccc", color: "#fff", border: "none", borderRadius: 4, fontWeight: 600, cursor: (acting || !rejectionNote.trim()) ? "default" : "pointer" }}
                >
                  {acting ? "Processing..." : "Confirm Rejection"}
                </button>
                <button
                  onClick={() => { setShowRejectForm(false); setRejectionNote(""); }}
                  disabled={acting}
                  style={{ padding: "9px 16px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Send to Vendor — creator or SuperAdmin, Approved status */}
      {canSend && (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: "2px solid #5B6A8A30" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#5B6A8A" }}>Dispatch to Vendor</h3>
          {!showSendConfirm ? (
            <button
              onClick={() => setShowSendConfirm(true)}
              disabled={acting}
              style={{ padding: "10px 28px", background: "#1B3A6B", color: "#fff", border: "none", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: acting ? "default" : "pointer" }}
            >
              ✉ Send to Vendor
            </button>
          ) : (
            <div style={{ padding: "16px", background: "#f0f4ff", border: "1px solid #b0c0e0", borderRadius: 6 }}>
              <p style={{ margin: "0 0 12px", fontSize: 13 }}>
                This will email the approved PO PDF to <strong>{po.vendorContactEmail}</strong> and CC <strong>{po.contactEmail}</strong>. Continue?
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => void handleSend()}
                  disabled={acting}
                  style={{ padding: "9px 20px", background: "#1B3A6B", color: "#fff", border: "none", borderRadius: 4, fontWeight: 600, cursor: acting ? "default" : "pointer" }}
                >
                  {acting ? "Sending..." : "Confirm Send"}
                </button>
                <button
                  onClick={() => setShowSendConfirm(false)}
                  disabled={acting}
                  style={{ padding: "9px 16px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mark as Received — creator or SuperAdmin, Sent status */}
      {canReceive && (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: "2px solid #5B6A8A30" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "#5B6A8A" }}>Goods / Services Receipt</h3>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#555" }}>Confirm that the goods or services for this order have been received.</p>
          <button
            onClick={() => void handleReceive()}
            disabled={acting}
            style={{ padding: "10px 28px", background: "#059669", color: "#fff", border: "none", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: acting ? "default" : "pointer" }}
          >
            {acting ? "Processing..." : "✓ Mark as Received"}
          </button>
        </div>
      )}

      {/* Back button */}
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid #eee" }}>
        <button onClick={onDone} style={{ padding: "9px 20px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", color: "#333" }}>
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
}
